import { useRef, useState } from 'react'
import { useEquipamentosNaCena } from './contextoEquipamentos'
import { CORES_USO_PONTO, pontosDeAncoragem } from '../../dominio/tabletop/ancoragem'
import type { ObjetoTabletop, TipoObjetoTabletop } from '../../dominio/tabletop/tipos'

/** Equipamentos do tamanho da mão: o marcador padrão cobriria o objeto inteiro. */
const TIPOS_PEQUENOS: ReadonlySet<TipoObjetoTabletop> = new Set(['polia', 'poliaDupla', 'mosquetao', 'bloqueador', 'pontoAncoragem', 'placaAncoragem', 'envolvimento', 'descensor', 'protetorCorda'])

const RAIO_PADRAO_M = 0.05
const RAIO_SECUNDARIO_M = 0.025
const RAIO_PEQUENO_M = 0.012
/** Área de toque dos pontos quando a ferramenta de corda os usa: mesmo o de uma polia de 10 cm precisa ser fácil de acertar. */
const RAIO_ALVO_M = 0.1
const LIMITE_ARRASTO_PX = 6

/**
 * Os marcadores dos pontos de ancoragem de um objeto.
 *
 * Fica DENTRO do grupo do objeto e usa as coordenadas locais dos pontos, então
 * acompanha o objeto ao mover, girar, escalar ou mudar um parâmetro, sem nenhum
 * cálculo extra aqui. Os marcadores só recebem toque quando a ferramenta de
 * corda está ativa (`aoEscolherPonto`): é ela quem escolhe os pontos.
 */
export function PontosAncoragem3D({
  objeto,
  aoEscolherPonto,
  idsEscolhidos,
}: {
  objeto: ObjetoTabletop
  /** Com isto os pontos viram alvos de toque; sem, são só marcadores. */
  aoEscolherPonto?: (idPonto: string) => void
  /** Pontos deste objeto que já fazem parte da corda em desenho. */
  idsEscolhidos?: readonly string[]
}) {
  const [sobre, setSobre] = useState<string | null>(null)
  const inicioToque = useRef<{ x: number; y: number } | null>(null)
  const pontos = pontosDeAncoragem(objeto)
  if (pontos.length === 0) return null
  const pequeno = TIPOS_PEQUENOS.has(objeto.tipo)
  // no modo de destaque a peça cresce em volta da origem, e os pontos vão junto
  const f = useEquipamentosNaCena().fatorDaPeca(objeto.id)

  return (
    <group name="pontos-ancoragem">
      {pontos.map((ponto) => {
        const raio = pequeno ? RAIO_PEQUENO_M : ponto.secundario ? RAIO_SECUNDARIO_M : RAIO_PADRAO_M
        const escolhido = idsEscolhidos?.includes(ponto.id) ?? false
        const destacado = sobre === ponto.id
        return (
          <group key={ponto.id} position={[ponto.local[0] * f, ponto.local[1] * f, ponto.local[2] * f]}>
            <mesh
              name={`ponto-${ponto.id}`}
              renderOrder={10}
              raycast={() => null}
              scale={destacado ? 1.8 : escolhido ? 1.4 : 1}
            >
              <sphereGeometry args={[raio, 12, 8]} />
              <meshBasicMaterial
                color={destacado || escolhido ? '#ffffff' : CORES_USO_PONTO[ponto.uso]}
                depthTest={false}
                transparent
                opacity={ponto.secundario && !destacado ? 0.7 : 0.95}
              />
            </mesh>
            {aoEscolherPonto && (
              <mesh
                name={`alvo-${ponto.id}`}
                onPointerDown={(evento) => {
                  evento.stopPropagation()
                  inicioToque.current = { x: evento.nativeEvent.clientX, y: evento.nativeEvent.clientY }
                }}
                onClick={(evento) => {
                  evento.stopPropagation()
                  const inicio = inicioToque.current
                  inicioToque.current = null
                  // o arrasto de órbita também termina sobre um ponto e não é escolha
                  if (inicio && Math.hypot(evento.nativeEvent.clientX - inicio.x, evento.nativeEvent.clientY - inicio.y) > LIMITE_ARRASTO_PX) return
                  aoEscolherPonto(ponto.id)
                }}
                onPointerOver={(evento) => {
                  evento.stopPropagation()
                  setSobre(ponto.id)
                  document.body.style.cursor = 'pointer'
                }}
                onPointerOut={() => {
                  setSobre((atual) => (atual === ponto.id ? null : atual))
                  document.body.style.cursor = ''
                }}
              >
                <sphereGeometry args={[RAIO_ALVO_M, 8, 6]} />
                <meshBasicMaterial transparent opacity={0} depthWrite={false} />
              </mesh>
            )}
          </group>
        )
      })}
    </group>
  )
}
